import { create } from 'zustand';
import { normalizeBottomTabs } from '@shared/lib/bottomTabs';
import type {
  CompiledMemberAppExperience,
  CompiledNavigationItem,
  CompiledMoreTileCategory,
  CompiledBannerConfig,
} from '../types/domainConfig';

/**
 * `CompiledMemberAppExperience` cache — drives the dynamic shell.
 * Backend ships per-tenant tabs, hamburger items, More-tile categories,
 * and banners; the React shell renders directly from this store rather
 * than from a hardcoded route table.
 */

const STORAGE_KEY = 'paas_app_experience';

/**
 * Stable empty references. The `get*()` selectors below are consumed
 * directly as Zustand selectors (`useExperienceStore((s) => s.getBottomTabs())`);
 * returning a fresh `[]` each call makes `useSyncExternalStore` see a new
 * snapshot every render → "Maximum update depth exceeded". Returning a
 * shared constant keeps the snapshot referentially stable.
 */
const EMPTY_NAV: CompiledNavigationItem[] = [];
const EMPTY_CATEGORIES: CompiledMoreTileCategory[] = [];

function withHomeFirst(experience: CompiledMemberAppExperience): CompiledMemberAppExperience {
  const bottom = experience.navigation?.bottom_tabs;
  if (!bottom?.length) return experience;
  return {
    ...experience,
    navigation: {
      ...experience.navigation!,
      bottom_tabs: normalizeBottomTabs(bottom),
    },
  };
}

interface ExperienceState {
  experience: CompiledMemberAppExperience | null;
  setExperience: (experience: CompiledMemberAppExperience | null) => void;
  getBottomTabs: () => CompiledNavigationItem[];
  getHamburger: () => CompiledNavigationItem[];
  getMoreCategories: () => CompiledMoreTileCategory[];
  getBanner: (bannerKey: string) => CompiledBannerConfig | undefined;
  hydrate: () => void;
}

export const useExperienceStore = create<ExperienceState>((set, get) => ({
  experience: null,

  setExperience(experience) {
    const next = experience ? withHomeFirst(experience) : null;
    set({ experience: next });
    try {
      if (next) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      console.warn('[experience] persist failed:', error);
    }
  },

  getBottomTabs() {
    return get().experience?.navigation?.bottom_tabs ?? EMPTY_NAV;
  },
  getHamburger() {
    return get().experience?.navigation?.hamburger ?? EMPTY_NAV;
  },
  getMoreCategories() {
    return get().experience?.tiles.more_categories ?? EMPTY_CATEGORIES;
  },
  getBanner(bannerKey) {
    return get().experience?.banners?.[bannerKey];
  },

  hydrate() {
    if (typeof window === 'undefined') return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const experience = withHomeFirst(JSON.parse(raw) as CompiledMemberAppExperience);
      set({ experience });
    } catch (error) {
      console.warn('[experience] hydrate failed:', error);
    }
  },
}));
